import React, { Suspense, useCallback, useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCity, faGlobeAmericas, faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import copy from "copy-to-clipboard";
import { partition } from "lodash";

import ViewportContext from "~/common/ViewportContext";
import { useFeature } from "~/lib/flippy";
import { RunQuery, useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import { HelpCenterIcon, HelpCenterLink, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import type { AttendeeAttrs } from "../../common/Attendees";
import { streamStore as store } from "../utils";
import { ShareSection } from "./ShareSection";
import StreamInviteRow from "./StreamInviteRow";
import UserAccessRow from "./UserAccessRow";

import type { ShareModal_RendererQuery as RendererQuery } from "~/__generated__/ShareModal_RendererQuery.graphql";
import type { ShareModalQuery as QueryType } from "~/__generated__/ShareModalQuery.graphql";
import type { stream_mainstream$data as MainStream } from "~/__generated__/stream_mainstream.graphql";

import styles from "../css/ShareModal.module.css";

const EXTERNAL_USER_NOTE_DONT_SHOW_AGAIN_KEY = "SharingModal__externals";

export interface RendererProps {
	streamId: string;
	ownerId: string;
	deferredExternals: readonly AttendeeAttrs[] | null;
	onClose: () => void;
	showSharingPaywall?: boolean;
	isFromZoom?: boolean;
	prepopulateDeferredExternals?: boolean;
	sharedNoteStreamUsers: MainStream["notestreamusers"];
	onShared?(newUserIds: string[], newEmails: string[]): void;
	hideGeneralAccess?: boolean;
}

const rendererQueryVariables = {};

export default function ShareModalRenderer(props: RendererProps) {
	const [result, setResult] = React.useState<RendererQuery["response"] | undefined>();
	const [error, setError] = React.useState<Error | undefined>();
	const isCrossWorkspaceFeatureEnabled = useFeature("cross_workspace_collaboration", "account");

	React.useEffect(() => {
		if (error) {
			toast.error("Something went wrong, please try again later.", { title: "Error" });
		}
	}, [error]);

	return (
		<>
			{/* change to query renderer */}
			<RunQuery<RendererQuery>
				onResult={setResult}
				onError={setError}
				variables={rendererQueryVariables}
				query={graphql`
					query ShareModal_RendererQuery {
						viewer {
							hasAdminAccess
							account {
								allowInviting
								guestUsersAllowInvite
								allowExternalInviting
								emailDomainList
							}
						}
					}
				`}
			/>
			{result && (
				<Suspense fallback={<LoadingSpinner dimmer={false} />}>
					<ShareModal
						allowInviting={!!result.viewer.account.allowInviting}
						allowGuestsInviting={result.viewer.account.guestUsersAllowInvite}
						allowExternalInviting={result.viewer.account.allowExternalInviting && !!isCrossWorkspaceFeatureEnabled}
						emailDomainList={result.viewer.account.emailDomainList}
						{...props}
					/>
				</Suspense>
			)}
		</>
	);
}

interface Props extends RendererProps {
	allowInviting: boolean;
	allowGuestsInviting: boolean | null;
	allowExternalInviting: boolean | null;
	prepopulateDeferredExternals?: boolean;
	emailDomainList: readonly string[];
}

const shareModalQuery = graphql`
	query ShareModalQuery($streamId: ID!) {
		viewer {
			user {
				id
				isGuest
			}
		}
		account {
			allowPublicLink
		}
		noteStream(id: $streamId) {
			id
			displayName
			isExternal
			publicLink {
				url
				enabled
			}
			pendingStreamInvites {
				id
				email
				isExpired
			}
			viewerNotestreamuser {
				id
				accessType
			}
		}
	}
`;

const changePublicAccessLevelMutation = graphql`
	mutation ShareModalPublicLinkMutation($id: ID!, $enabled: Boolean!) {
		noteStreamTogglePublicLink(id: $id, enabled: $enabled) {
			noteStream {
				id
				index
				publicLink {
					url
					enabled
				}
			}
		}
	}
`;

const publicAccessLevelOptions: SelectorOption[] = [
	{ label: "Can view", value: "VIEW" },
	{ label: "No access", value: "NO_ACCESS" },
];

function ShareModal(props: Props) {
	const {
		prepopulateDeferredExternals,
		deferredExternals,
		onClose,
		allowInviting,
		allowGuestsInviting,
		allowExternalInviting,
		showSharingPaywall,
		isFromZoom,
		sharedNoteStreamUsers,
		hideGeneralAccess,
		ownerId,
		onShared,
		emailDomainList,
	} = props;

	const viewport = React.useContext(ViewportContext);

	const [showExternalUserNote, setShowExternalUserNote] = useState(!store.has(EXTERNAL_USER_NOTE_DONT_SHOW_AGAIN_KEY));
	const [deletedUsers, setDeletedUsers] = useState<Set<string>>(new Set());

	const data = useLazyLoadQuery<QueryType>(shareModalQuery, { streamId: props.streamId }, {});
	const { account, noteStream, viewer } = data;
	const { allowPublicLink } = account;
	const [userAccessAllNotes, userAccessSomeNotes] = useMemo(() => {
		let [userAllNotes, userSomeNotes] = partition(sharedNoteStreamUsers, user => user.accessType !== "partial");
		userAllNotes = userAllNotes.filter(noteuser => !deletedUsers.has(noteuser.user.id));
		userSomeNotes = userSomeNotes.filter(noteuser => !deletedUsers.has(noteuser.user.id));
		return [userAllNotes, userSomeNotes];
	}, [sharedNoteStreamUsers, deletedUsers]);

	const externalWorkspaces = useMemo(() => {
		const accountsSet = new Set();
		const workspaces: Array<NonNullable<MainStream["notestreamusers"][0]["user"]["account"]>> = [];
		userAccessAllNotes.forEach(noteUser => {
			if (!noteUser.user.isExternal || !noteUser.user.account?.id || accountsSet.has(noteUser.user.account.id)) return;
			accountsSet.add(noteUser.user.account.id);
			workspaces.push(noteUser.user.account);
		});
		return workspaces;
	}, [userAccessAllNotes]);

	const onRemoveUser = useCallback(
		userId => {
			const newDeletedUsers = new Set(deletedUsers);
			newDeletedUsers.add(userId);
			setDeletedUsers(newDeletedUsers);
		},
		[deletedUsers],
	);

	const readOnly =
		!noteStream ||
		noteStream.isExternal ||
		!noteStream.viewerNotestreamuser ||
		noteStream.viewerNotestreamuser.accessType == "partial";

	const dismissExternalUserNote = useCallback(() => {
		store.set(EXTERNAL_USER_NOTE_DONT_SHOW_AGAIN_KEY, true);
		setShowExternalUserNote(false);
	}, []);

	const copyLink = useCallback(() => {
		copy(noteStream?.publicLink?.url || "");
		toast.success("Copied to clipboard!");
	}, [noteStream?.publicLink?.url]);

	const changePublicAccessLevel = useCallback(
		(accessLevelOption: SingleValue<SelectorOption>) => {
			if (!accessLevelOption || readOnly) return;

			commitMutation(environment, {
				mutation: changePublicAccessLevelMutation,
				variables: {
					id: noteStream?.id,
					enabled: accessLevelOption.value === "VIEW",
				},
				onCompleted: () => {
					toast.success("Changes saved successfully.");
				},
			});
		},
		[noteStream?.id, readOnly],
	);

	const onSharedFinished = useCallback(
		(newUserIds: string[], newEmails: string[]) => {
			onShared?.(newUserIds, newEmails);
			onClose();
		},
		[onShared, onClose],
	);

	const pendingStreamInvitesSet = useMemo(() => {
		const newSet = new Set();
		if (!noteStream) return newSet;
		noteStream.pendingStreamInvites?.forEach(invite => newSet.add(invite.email));
		return newSet;
	}, [noteStream]);

	const filteredDeferredExternals = useMemo(() => {
		if (!deferredExternals) return [];
		return deferredExternals.filter(deferredExternal => !pendingStreamInvitesSet.has(deferredExternal.email));
	}, [deferredExternals, pendingStreamInvitesSet]);

	if (!noteStream) return null;
	const { displayName, publicLink, pendingStreamInvites } = noteStream;
	const streamId = noteStream.id;

	return (
		<Modal onClose={onClose} width="31.25rem">
			<Modal.Header noBottomBorder>
				<Modal.Title>Share {displayName}</Modal.Title>
				<Modal.HeaderActions onClose={onClose}>
					<HelpCenterIcon articleId="5690305" title="Sharing notes and streams" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				{isFromZoom && (
					<div className={styles.private}>
						<h6>
							You just invited people to use the Fellow Zoom app! If you{"'"}d like to collaborate on this note
							together, share the series with them below.
						</h6>
					</div>
				)}
				{allowGuestsInviting && showExternalUserNote && (
					<div className={styles.guests}>
						<FontAwesomeIcon icon={faInfoCircle} />
						<h6>
							<HelpCenterLink articleId="6782177">Learn more</HelpCenterLink> about adding guest users from outside{" "}
							<em>{window.INITIAL_STATE.account?.name}</em>
						</h6>
						<a href="#" onClick={dismissExternalUserNote} className={styles.dismissButton}>
							Dismiss
						</a>
					</div>
				)}

				{/* GENERAL ACCESS SECTION */}
				{!hideGeneralAccess && (
					<div className={cx(styles.paddedSection, styles.withBorder)}>
						<h6 className={styles.subtitle}>General Access</h6>
						{externalWorkspaces.map(externalWorkspace => (
							<div className={styles.generalAccessRow} key={externalWorkspace.id}>
								<span className={styles.iconContainer}>
									<FontAwesomeIcon icon={faCity} className={styles.icon} />
								</span>
								<div style={{ flexGrow: 1 }}>
									<span style={{ fontSize: "14px" }}>{externalWorkspace.name}</span>
									<span className={styles.subtext}>Only the people listed below have access</span>
								</div>
							</div>
						))}
						<div className={styles.generalAccessRow}>
							<span className={styles.iconContainer}>
								<FontAwesomeIcon
									icon={faGlobeAmericas}
									className={cx(styles.icon, { [styles.iconEnabled]: allowPublicLink && publicLink?.enabled })}
								/>
							</span>
							<div style={{ flexGrow: 1 }}>
								<div className={styles.generalAccessTitle}>Public</div>
								<div className={styles.generalAccessSubtitle}>
									{!allowPublicLink
										? "Public links are turned off for your workspace"
										: publicLink?.enabled
											? "Anyone with the link..."
											: "No public access"}
								</div>
							</div>
							{allowPublicLink && publicLink?.enabled && (
								<Button variant="primary" className={styles.copyLink} onClick={copyLink}>
									{viewport.isMobile ? (
										<FontAwesomeIcon icon={faLink} className={styles.copyLinkIcon} />
									) : (
										<>Copy link</>
									)}
								</Button>
							)}
							{allowPublicLink ? (
								<Selector
									condensed
									options={publicAccessLevelOptions}
									value={Boolean(publicLink?.enabled) ? publicAccessLevelOptions[0] : publicAccessLevelOptions[1]}
									onChange={changePublicAccessLevel}
									className={styles.publicAccessLevelSelector}
									isDisabled={readOnly}
								/>
							) : (
								<span className={styles.lockedAccess}>No access</span>
							)}
						</div>
					</div>
				)}
				{/* SHARE SECTION */}
				{!readOnly && (
					<div className={cx(styles.paddedSection, styles.withBorder)}>
						<ShareSection
							streamId={streamId}
							showSharingPaywall={showSharingPaywall}
							prepopulateDeferredExternals={prepopulateDeferredExternals ?? false}
							hideGeneralAccess={hideGeneralAccess ?? false}
							deferredExternals={filteredDeferredExternals}
							allowExternalInviting={allowExternalInviting}
							allowGuestInviting={allowGuestsInviting}
							allowInternalInviting={allowInviting}
							sharedNoteStreamUsers={sharedNoteStreamUsers}
							pendingStreamInvites={pendingStreamInvites}
							onShared={onSharedFinished}
							readOnly={readOnly}
							emailDomainList={emailDomainList}
						/>
					</div>
				)}
				{/* USER ACCESS SECTION */}
				<div className={cx(styles.paddedSection, styles.withBorder)}>
					<div className={styles.userAccessSection}>
						<div className={cx(styles.subtext, styles.sectionHeader)}>Access to all notes and recaps</div>
						{userAccessAllNotes?.map(noteUser => (
							<UserAccessRow
								key={noteUser.id}
								viewer={viewer}
								user={noteUser.user}
								streamId={streamId}
								isViewer={noteUser.user.id === viewer.user.id}
								isOwner={noteUser.user.id === ownerId}
								readOnly={readOnly}
								accessType={noteUser.accessType}
								onRemove={onRemoveUser}
							/>
						))}
						{noteStream.pendingStreamInvites?.map(pendingInvite => (
							<StreamInviteRow
								key={pendingInvite.id}
								user={{ id: pendingInvite.id, email: pendingInvite.email, isExternal: true }}
								viewer={viewer}
								streamId={streamId}
								readOnly={readOnly}
								isExpired={pendingInvite.isExpired}
							/>
						))}
					</div>
					{userAccessSomeNotes.length > 0 && (
						<div className={styles.userAccessSection}>
							<div className={cx(styles.subtext, styles.sectionHeader)}>Access to some notes and recaps</div>
							{userAccessSomeNotes?.map(noteUser => (
								<UserAccessRow
									key={noteUser.id}
									user={noteUser.user}
									viewer={viewer}
									streamId={streamId}
									isViewer={noteUser.user.id === viewer.user.id}
									isOwner={noteUser.user.id === ownerId}
									readOnly={readOnly}
									accessType={noteUser.accessType}
									onRemove={onRemoveUser}
								/>
							))}
						</div>
					)}
				</div>
			</Modal.Body>
		</Modal>
	);
}
